<template>
	<section class="user-page">
		<div class="custom-button-container">
			<TableSearch :tableTitles="tableTitles" @input="handleInput" />
			<Button v-if="$route.name === 'users' || $route.name === 'user'" @click="openModal" skin="primary" :disabled="!loggedUser || isLoading">{{ $t('add_user') }}</Button>
		</div>
		<Tabs :isRTL="isRTL" syncRoute>
			<Tab :label="$t('users')" :to="'/admin/user'" exact>
				<template v-if="$route.name === 'users' || $route.name === 'user'">
					<Loading class="loader" v-if="isLoading" />
					<Table
						class="table"
						idField="id"
						v-if="usersToShow.length && !isLoading"
						:tableData="usersToShow"
						:editOptions="{ remove: true, edit: true }"
						@editRow="openModal"
						@removeRow="removeUser"
						:isPaginated="true"
						:itemsPerPage="10"
					/>
					<div v-if="!isLoading && !usersToShow.length" class="no-users">
						<h3>{{ $t('no_user_found') }}</h3>
					</div>
				</template>
			</Tab>
			<Tab :label="$t('cleaners')" :to="'/admin/user/cleaners'" exact>
				<template v-if="$route.name === 'cleaners'">
					<Loading class="loader" v-if="isLoadingEmployees" />
					<Table
						class="table"
						idField="id"
						v-if="filteredEmployees.length && !isLoadingEmployees"
						:tableData="filteredEmployees"
						:extraActions="[{ name: 'survey/eye', type: 'view-cleaner' }]"
						@action="handleCleanerAction"
						:isPaginated="true"
						:itemsPerPage="10"
					/>
					<div v-if="!isLoadingEmployees && !filteredEmployees.length" class="no-users">
						<h3>{{ $t('no_data_available') }}</h3>
					</div>
				</template>
			</Tab>
		</Tabs>
		<Modal
			class="modal"
			v-if="currUser"
			@submit="saveUser"
			@close="closeModal"
			width="medium"
			:texts="{ headerPrimary: currUser.id ? 'edit_user' : 'new_user' }"
			:isConfirmDisabled="isConfirmDisabled">
			<template v-slot:default>
				<div class="modal-body">
					<div v-for="(field, idx) in fields" :key="idx" class="short-field">
						<label>
							{{ $t(field.label) }}
							<Input
								v-model="currUser[field.model]" 
								:placeholder="`${$t(field.label)}...`" 
								:type="field.type === 'password' && isPasswordShown ? 'text' : field.type"
								:flash="errors.includes(field.model)"
							/>
						</label>
					</div>
					<label class="short-field password-container">
						{{ $t('password') }}
						<div>
							<Input
								v-model="currUser.password"
								:type="isPasswordShown ? 'text' : 'password'"
								:placeholder="`${$t('password')}...`"
								:style="{ marginBottom: '0px' }"
								:flash="errors.includes('password')"
							/>
							<img
								:src="require('@/assets/survey/eye.svg')"
								alt="eye"
								@mousedown="isPasswordShown = true"
								@mouseup="isPasswordShown = false"
							/>
						</div>
					</label>
					<label class="short-field">
						{{ $t('email') }}
						<Input
							v-model="currUser.email"
							:placeholder="`${$t('email')}...`"
							:style="{ marginBottom: '0px' }"
							@blur="checkEmail"
							:flash="errors.includes('email')"
						/>
					</label>
					<div class="select-container">
						{{ $t('type') }}
						<Select v-model="currUser.type" class="modal-select">
							<Option value="admin" label="admin" />
							<Option value="viewer" label="viewer" />
							<Option value="installer" label="installer" />
						</Select>
						<ErrorMsg :isShown="errors.includes('type')" :text="`${$t('must_choose')}`" />
					</div>
					<div class="select-container">
						{{ $t('level') }}
						<Select v-model="currUser.level" class="modal-select" :placeholder="$t('level')">
							<Option
								v-for="(level, idx) in loggedUser.permitedLevels"
								:key="level + idx"
								:value="level"
								:label="$t(level)"
							/>
						</Select>
					</div>
					<div class="select-container">
						{{ $t('job_function') }}
						<Select v-model="currUser.jobFunction" class="modal-select" :placeholder="$t('job_function')">
							<Option
								v-for="(jobFunction, idx) in jobFunctions"
								:key="jobFunction + idx"
								:value="jobFunction"
								:label="$t(jobFunction)"
							/>
						</Select>
					</div>
					<div class="select-container">
						{{ $t('language') }}
						<Select v-model="currUser.language" class="modal-select" :placeholder="$t('language')">
							<Option
								v-for="(lang, idx) in selectOptions.languages"
								:key="lang.value + idx"
								:value="lang.value"
								:label="lang.label"
							/>
						</Select>
						<ErrorMsg :isShown="errors.includes('language')" :text="`${$t('must_choose')}`" />
					</div>
					<div class="select-container">
						{{ $t('service_provider') }}
						<Select 
							v-model="currUser.serviceProviderId"
							@input="handleServiceProvider(currUser.serviceProviderId)"
							class="modal-select"
							:placeholder="$t('service_provider')"
							>
							<Option
								v-for="(sp, idx) in selectOptions.serviceProviders"
								:key="sp.id + '' + idx"
								:value="sp.id"
								:label="sp.name"
							/>
						</Select>
					</div>
					<transition name="fade" v-for="(field, idx) in multiSelectFields" :key="field + idx">
						<div class="select-container">
							{{ $t(field) }}
							<Select
								:multiple="true"
								:placeholder="$t('select')"
								:value="getUserSelectedValues(field)"
								@input="handleSelect($event, field)"
								class="modal-select"
								>
								<SearchBar v-model="modalSearchBy[field]" />
								<!-- Add the "all" option -->
								<Option value="all" :label="$t('all')" :key="`all${field}`" />
								<Option
									v-for="(option, idx) in getFilteredItems(selectOptions[`${field}_options`], field)"
									:key="option.id + '' + idx"
									:value="option.id"
									:label="$t(option.name)"
								/>
							</Select>
						</div> 
					</transition>
				</div>
			</template>
		</Modal>
		<Modal
			class="modal"
			v-if="currCleaner"
			@close="closeCleanerModal"
			width="medium"
			:texts="{ headerPrimary: 'cleaner' }"
			:showConfirmBtn="false">
			<template v-slot:default>
				<div class="modal-body cleaner-details">
					<div class="detail-section">
						<div class="detail-row">
							<label class="detail-label">{{ $t('phone') }}</label>
							<span class="detail-value">{{ currCleaner.phone || $t('not_available') }}</span>
						</div>
						<div class="detail-row">
							<label class="detail-label">{{ $t('username') }}</label>
							<span class="detail-value">{{ currCleaner.username || $t('not_available') }}</span>
						</div>
						<div class="detail-row">
							<label class="detail-label">{{ $t('created_at') }}</label>
							<span class="detail-value">{{ currCleaner.createdAt ? new Date(currCleaner.createdAt).toLocaleDateString() : $t('not_available') }}</span>
						</div>
					</div>
					<div class="detail-section sites-section">
						<label class="detail-label sites-label">{{ $t('sites') }}</label>
						<div class="sites-list" v-if="currCleaner.siteNames && currCleaner.siteNames !== 'N/A' && currCleaner.siteNames !== $t('not_available')">
							<div class="site-item" v-for="(site, index) in getSitesList(currCleaner.siteNames)" :key="index">
								<span class="site-bullet"></span>
								<span class="site-name">{{ site }}</span>
							</div>
						</div>
						<div class="sites-list" v-else>
							<span class="no-sites">{{ $t('not_available') }}</span>
						</div>
					</div>
				</div>
			</template>
		</Modal>
	</section>
</template>

<script>
import Input from '@/modules/common/components/Input'
import Loading from '@/modules/common/components/Loading'
import Select from '@/modules/common/components/Select'
import ErrorMsg from '@/modules/common/components/ErrorMsg'
import Option from '@/modules/common/components/Option'
import swalService from '@/modules/common/services/swalService'
import Modal from '@/modules/common/components/Modal'
import Table from '@/modules/common/components/customTable/Table'
import TableSearch from '@/modules/common/components/customTable/TableSearch'
import Button from '@/modules/common/components/Button'
import Tabs from '@/modules/common/components/Tabs/Tabs'
import Tab from '@/modules/common/components/Tabs/Tab'
import siteService from '../services/siteService'
import { getUsers, getUser, getPermitedLevels, saveUser, removeUser, getLanguages } from '../services/userService'
import { getServiceProviders } from '../services/serviceProviderService'
import authService from '../../auth/services/authService'
import userService from '../services/userService'
import util from '../../common/services/utilService'
import SearchBar from '@/modules/common/components/SearchBar'
import { getFilterdContacts } from '../services/contactService'
import { mapGetters } from 'vuex'
import { DEVICE_ACTIONS_PERMISSION, panelPermittedOptions } from '@/modules/common/consts/consts'

export default {
	async created() {
		await this.loadUsers()
		this.setTableTitles()
	},
		data() {
		return {
			currUser: null,
			currCleaner: null,
			isLoading: false,
			isLoadingEmployees: false,
			users: [],
			employees: [],
			isPasswordShown: false,
			selectOptions: this.defaultSelectOptions(),
			loggedUser: null,
			openSelectMenus: {
				permitted_franchise: false,
				permitted_companies: false,
				permitted_sites: false,
				panel_permitted: false
			},
			errors: [],
			tableTitles: null,
			searchTxt: '',
			searchBy: '',
			modalSearchBy: this.defaultModalSearchBy(),
			isConfirmDisabled: false,
			permittedSitesIds: [],
			permittedCompaniesIds: [],
			permittedFranchiseIds: [],
			permittedPanelViews: [],
			isHydratingPermitted: false,
			jobFunctions: [
				'property_manager',
				'facility_manager',
				'site_manager',
				'workplace_manager',
				'cleaning_company_ceo',
				'operations_manager',
				'area_manager',
				'site_cleaning_manager',
				'cleaning_supervisor',
				'other'
			],
			fields: [
				{ label: 'first_name', model: 'firstName', type: 'text' },
				{ label: 'last_name', model: 'lastName', type: 'text' },
				{ label: 'username', model: 'username', type: 'text' },
				{ label: 'phone', model: 'phone', type: 'tel' },
			],
			originalFields :{
				email: '',
				username: '',
				phone: '',
			},
		}
	},
	computed: {
		...mapGetters(['isRTL']),
		usersToShow() {
			const { searchTxt, searchBy } = this
			const users = this.users.map(({ id, email, username, firstName, lastName, level, type, phone, jobFunction }) => ({
				id,
				email,
				username,
				firstName,
				lastName,
				level,
				job_function: jobFunction ? this.$t(jobFunction) : '',
				type,
				phone
			}))
			return util.filterArrayOfObjectsWithString(users, searchTxt, searchBy)
		},
		filteredEmployees() {
			return util.filterArrayOfObjectsWithString(this.employeesToShow, this.searchTxt, this.searchBy)
		},
		employeesToShow() {
			return this.employees.map((employee) => {
				const {
					id,
					phone,
					username
				} = employee
				return {
					id,
					phone: phone || 'N/A',
					username: username || 'N/A'
				}
			})
		},
		gridRowNumber() {
			const { level } = this.loggedUser
			if (level === 'buzzz') return 5
			else if (level === 'franchise') return 4
			else return 3
		},
		multiSelectFields() {
			const fields = ['sites', 'companies', 'franchise']
			let fieldsToShow = ['panel_permitted']
			for (let i = 0; i < this.gridRowNumber - 2; i++) {
				fieldsToShow.unshift(`permitted_${fields[i]}`)
			}
			return fieldsToShow
		},
	},
	methods: {
		async checkEmail() {
			this.errors = []
			const { email, id } = this.currUser
			if (id) return
			if (!email) {
				this.errors.push('email')
				return
			}
			const isValid = await authService.isEmailValid(email)
			if (!isValid) this.errors.push('email')
		},
		closeModal() {
			this.errors = []
			this.currUser = null
			this.isHydratingPermitted = false
			this.resetPermittedSelectedValues()
			this.selectOptions = this.defaultSelectOptions()
		},
		async openModal(user) {
			// Check if user is actually an event object (from button click) or a real user object
			const isEvent = user && (user instanceof Event || user.type === 'click' || user.target)
			const actualUser = isEvent ? null : user
			
			// Ensure loggedUser is loaded before proceeding
			if (!this.loggedUser || !this.loggedUser.level) {
				if (this.isLoading) {
					// Button should be disabled, but handle edge case
					return
				}
				await this.loadUsers()
				if (!this.loggedUser || !this.loggedUser.level) {
					swalService.errorMsg()
					return
				}
			}
			
			// If adding a new user, set currUser so modal opens
			if (!actualUser) {
				const copiedPanelPermissions = Array.isArray(this.selectOptions.panel_permitted_options)
					? JSON.parse(JSON.stringify(this.selectOptions.panel_permitted_options))
					: []
				const defaultPanelPermitted = copiedPanelPermissions.filter((option) => option.id !== DEVICE_ACTIONS_PERMISSION)
				this.currUser = {
					email: '',
					password: '',
					firstName: '',
					lastName: '',
					username: '',
					address: '',
					permitted_franchise: [],
					permitted_companies: [],
					permitted_sites: [],
					panel_permitted: defaultPanelPermitted,
					phone: '',
					level: 'site',
					type: '',
					jobFunction: '',
					sites: [],
					serviceProviderId: '',
				}
			}
			
			try {
				
				const { options, field } = await userService.getFirstOptions(this.loggedUser.level)
				this.selectOptions[field] = options
				const [languages, serviceProviders] = await Promise.all([getLanguages(), getServiceProviders()])
				this.selectOptions.languages = languages
				this.selectOptions.serviceProviders = serviceProviders
				this.selectOptions.serviceProviders.unshift({ id: '', name: 'none' })

				if (actualUser) {
					const userData = await getUser(actualUser.id)
					this.currUser = userData
					this.originalFields = {
						email: userData.email,
						username: userData.username,
						phone: userData.phone,
					}
					await this.hydratePermittedSelections(userData)
				} else {
					this.updatePermittedSelectedValues()
				}
			} catch (error) {
				console.error('Error opening modal:', error)
				swalService.errorMsg()
			}
		},
		async removeUser(id) {
			const result = await swalService.confirmMsg()

			if (!result.value) return
			try {
				await removeUser(id)
				swalService.deletedMsg()
			} catch (err) {
				swalService.errorMsg()
			}

			this.loadUsers()
		},
		async checkForm() {
			this.errors = []

			const restrictedUsernames = ['admin', 'viewer', 'installer', 'supervisor']
			const {
				email,
				firstName,
				lastName,
				username,
				password,
				permitted_sites,
				id,
				type,
				phone,
				language,
				level
			} = this.currUser

			const { email: originalEmail, username: originalUsername, phone: originalPhone } = this.originalFields
			
			// Helper to check and add error
			const addErrorIfFalsy = (condition, errorField) => {
				if (!condition) this.errors.push(errorField)
			}


			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
			const usernameRegex = /^(?=.*[a-zA-Z])[a-zA-Z0-9]+$/g
			const phoneRegex = /^\d+$/
			
			// Field presence validation
			addErrorIfFalsy(firstName, 'firstName')
			addErrorIfFalsy(lastName, 'lastName')
			addErrorIfFalsy(username && !restrictedUsernames.includes(username) && usernameRegex.test(username), 'username')
			addErrorIfFalsy(password || id, 'password')
			addErrorIfFalsy(email && emailRegex.test(email), 'email')
			addErrorIfFalsy(level, 'level')
			addErrorIfFalsy(phone && phoneRegex.test(phone) && phone.length >= 10, 'phone')
			addErrorIfFalsy(type, 'type')
			addErrorIfFalsy(language, 'language')
			if (level !== 'franchise' && (!permitted_sites || !Array.isArray(permitted_sites) || !permitted_sites.length)) this.errors.push('sites')

			const validations = {
				isEmailValidPrm: (!id || email !== originalEmail) && email ? authService.isEmailValid(email) : true,
				isUsernameValidPrm: (!id || username !== originalUsername) && username ? authService.isUsernameValid(username) : true,
				isPhoneValidPrm: (!id || phone !== originalPhone) && phone ? authService.isPhoneValid(phone) : true
			}

			try {
				const [isEmailValid, isUsernameValid, isPhoneValid] = await Promise.all(Object.values(validations))

				if (!isEmailValid) this.errors.push('email')
				if (!isUsernameValid) this.errors.push('username')
				if (!isPhoneValid) this.errors.push('phone')
			} catch (error) {
				// Handle error
			}
		},
		async saveUser() {
			const { currUser } = this
			const user = { ...currUser }
			await this.checkForm()
			if (this.errors.length) return
			this.isConfirmDisabled = true
			if (user.panel_permitted && Array.isArray(user.panel_permitted) && user.panel_permitted.length) {
				user.panelViews = user.panel_permitted.map((a) => a.name)
			} else {
				user.panelViews = []
			}
			if (user.permitted_sites && Array.isArray(user.permitted_sites) && user.permitted_sites.length) {
				user.sites = user.permitted_sites.map((s) => s.id)
			} else {
				user.sites = []
			}
			try {
				await saveUser(user)
				this.isConfirmDisabled = false
				swalService.savedMsg(this.$t('Your work has been saved'))
			} catch (err) {
				swalService.errorMsg()
			}
			this.resetModalSearchBy()
			this.closeModal()
			this.loadUsers()
		},
	updatePermittedSelectedValues() {
		const { currUser } = this
		if (!currUser) return
		const { permitted_sites, permitted_companies, permitted_franchise, panel_permitted } = currUser
		if (permitted_sites && Array.isArray(permitted_sites) && permitted_sites.length > 0) {
			this.permittedSitesIds = [...new Set(permitted_sites.map(s => s.id))]
		} else {
			this.permittedSitesIds = []
		}
		if (permitted_companies && Array.isArray(permitted_companies) && permitted_companies.length > 0) {
			this.permittedCompaniesIds = [...new Set(permitted_companies.map(c => c.id))]
		} else {
			this.permittedCompaniesIds = []
		}
		if (permitted_franchise && Array.isArray(permitted_franchise) && permitted_franchise.length > 0) {
			this.permittedFranchiseIds = [...new Set(permitted_franchise.map(f => f.id))]
		} else {
			this.permittedFranchiseIds = []
		}
		if (panel_permitted && Array.isArray(panel_permitted) && panel_permitted.length > 0) {
			this.permittedPanelViews = [...new Set(panel_permitted.map(p => p.name))]
		} else {
			this.permittedPanelViews = []
		}
	},
		resetPermittedSelectedValues() {
			this.permittedSitesIds = []
			this.permittedCompaniesIds = []
			this.permittedFranchiseIds = []
		},
		getPermittedDescendantFields(field) {
			const descendants = {
				permitted_franchise: ['permitted_companies', 'permitted_sites'],
				permitted_companies: ['permitted_sites'],
			}
			return descendants[field] || []
		},
		clearPermittedDescendants(field) {
			this.getPermittedDescendantFields(field).forEach((childField) => {
				this.currUser[childField] = []
			})
			if (field === 'permitted_franchise') {
				this.selectOptions.permitted_companies_options = []
				this.selectOptions.permitted_sites_options = []
			} else if (field === 'permitted_companies') {
				this.selectOptions.permitted_sites_options = []
			}
		},
		async hydratePermittedSelections(userData) {
			const hydrateOpts = { skipUiSync: true, skipCascade: true }
			this.isHydratingPermitted = true
			try {
				if (userData.panelViews && Array.isArray(userData.panelViews)) {
					await this.handleMultiSelectInput(
						userData.panelViews.map((a) => ({ id: a, name: a })),
						'panel_permitted',
						hydrateOpts,
					)
				}
				if (userData.franchises && Array.isArray(userData.franchises)) {
					await this.handleMultiSelectInput(userData.franchises, 'permitted_franchise', hydrateOpts)
				}
				if (userData.companies && Array.isArray(userData.companies)) {
					await this.handleMultiSelectInput(userData.companies, 'permitted_companies', hydrateOpts)
				}
				if (userData.sites && Array.isArray(userData.sites)) {
					await this.handleMultiSelectInput(userData.sites, 'permitted_sites', hydrateOpts)
				}
			} finally {
				this.isHydratingPermitted = false
				this.updatePermittedSelectedValues()
			}
		},
		async syncPermittedChildren(changedField) {
			if (!this.currUser) return

			if (changedField === 'permitted_franchise' && this.multiSelectFields.includes('permitted_companies')) {
				const validCompanyIds = new Set(
					(this.selectOptions.permitted_companies_options || []).map((c) => c.id),
				)
				this.currUser.permitted_companies = (this.currUser.permitted_companies || []).filter((c) =>
					validCompanyIds.has(c.id),
				)

				if (this.currUser.permitted_companies.length) {
					const { options } = await userService.getNextOptions(
						'permitted_companies',
						this.currUser.permitted_companies.map((c) => c.id),
					)
					this.selectOptions.permitted_sites_options = options
				} else {
					this.selectOptions.permitted_sites_options = []
				}
			}

			if (
				(changedField === 'permitted_franchise' || changedField === 'permitted_companies') &&
				this.multiSelectFields.includes('permitted_sites')
			) {
				const validSiteIds = new Set((this.selectOptions.permitted_sites_options || []).map((s) => s.id))
				this.currUser.permitted_sites = (this.currUser.permitted_sites || []).filter((s) => validSiteIds.has(s.id))
			}
		},
		async handleMultiSelectInput(values, field, { skipUiSync = false, skipCascade = false } = {}) {
			if (!values || !values.length) {
				this.currUser[field] = []
				if (field !== 'panel_permitted' && !skipCascade) {
					this.clearPermittedDescendants(field)
				}
				if (!skipUiSync) {
					this.updatePermittedSelectedValues()
				}
				return
			}
			this.currUser[field] = JSON.parse(JSON.stringify(values))
			if (field !== 'panel_permitted') {
				const { nextField, options } = await userService.getNextOptions(field, values.map((a) => a.id))
				if (nextField) {
					this.selectOptions[nextField] = options
				}
				if (!skipCascade) {
					await this.syncPermittedChildren(field)
				}
			}
			if (!skipUiSync) {
				this.updatePermittedSelectedValues()
			}
		},
		async handleServiceProvider(id) {
			if (!id) {
				this.resetCurrUserPermittedOptions()
				return
			}
			const { selectOptions: { serviceProviders } } = this
			const selectedServiceProvider = serviceProviders.find((sp) => sp.id === id)
			const { franchiseId, franchiseName, associated_companies: associatedCompanies, sites } = selectedServiceProvider
			await Promise.all([
				this.handleMultiSelectInput([{ id: franchiseId, name: franchiseName }], 'permitted_franchise'),
				this.handleMultiSelectInput(associatedCompanies, 'permitted_companies'),
				this.handleMultiSelectInput(sites, 'permitted_sites')
			])
			this.updatePermittedSelectedValues()
		},
		defaultSelectOptions() {
			return {
				permitted_franchise_options: [],
				permitted_companies_options: [],
				permitted_sites_options: [],
				languages: [],
				panel_permitted_options: panelPermittedOptions.map((option) => ({ ...option })),
				serviceProviders: [],
			}
		},
		resetCurrUserPermittedOptions() {
			this.currUser.permitted_franchise = []
			this.currUser.permitted_companies = []
			this.currUser.permitted_sites = []
			this.resetPermittedSelectedValues()
		},
		async handleSelect(selectedValues, field) {
			if (this.isHydratingPermitted) {
				return
			}
			const fieldItems = this.selectOptions[`${field}_options`]
			if (!fieldItems || !Array.isArray(fieldItems)) {
				return
			}
			if (this.arePermittedValuesUnchanged(selectedValues, field)) {
				return
			}
			const isAllSelected = selectedValues.includes('all')
			if (isAllSelected) {
				if (this.getUserSelectedValues(field).length === fieldItems.length) {
					await this.handleMultiSelectInput([], field)
				} else {
					const selectedItems = fieldItems
					await this.handleMultiSelectInput(selectedItems, field)
				}
			} else {
				const selectedItems = field === 'panel_permitted'
					? fieldItems.filter((item) => selectedValues.includes(item.name))
					: fieldItems.filter((item) => selectedValues.includes(item.id))
				await this.handleMultiSelectInput(selectedItems, field)
			}
			this.openSelectMenus[field] = !this.openSelectMenus[field]
		},
		arePermittedValuesUnchanged(selectedValues, field) {
			const currentValues = this.getUserSelectedValues(field)
			if (!Array.isArray(selectedValues) || !Array.isArray(currentValues)) {
				return false
			}
			if (selectedValues.length !== currentValues.length) {
				return false
			}
			const normalizedSelected = field === 'panel_permitted'
				? [...selectedValues].sort()
				: [...selectedValues].map(Number).sort((a, b) => a - b)
			const normalizedCurrent = field === 'panel_permitted'
				? [...currentValues].sort()
				: [...currentValues].map(Number).sort((a, b) => a - b)
			return normalizedSelected.every((value, index) => value === normalizedCurrent[index])
		},
		getUserSelectedValues(field) {
			if (field === 'panel_permitted') {
				return this.permittedPanelViews
			}
			if (field === 'permitted_sites') {
				return this.permittedSitesIds
			}
			if (field === 'permitted_companies') {
				return this.permittedCompaniesIds
			}
			if (field === 'permitted_franchise') {
				return this.permittedFranchiseIds
			}
		},
		async loadUsers() {
			try {
				this.isLoading = true
				const users = await getUsers()
				if (!users.length) {
					this.isLoading = false
					return
				}
				this.users = users
				const loggedUser = { ...this.$store.getters.loggedUser }
				const permitedSites = await siteService.getSites(loggedUser.siteIdsToShow)
				loggedUser.permitedSites = permitedSites
				loggedUser.permitedLevels = getPermitedLevels(loggedUser.level)
				this.loggedUser = loggedUser
				this.isLoading = false
				return loggedUser
			} catch (err) {
				swalService.errorMsg()
			}
		},
		setTableTitles() {
			if (this.$route.name === 'cleaners') {
				const employeesToShow = this.employeesToShow
				if (!employeesToShow.length) {
					this.tableTitles = []
					return
				}
				const excludedKeys = ['id', 'sub', 'roomIds', 'shiftIds', 'appPagesIds', 'activeSiteIds', 'workingAreaIds', '_employeeData', 'created_at', 'createdAt']
				this.tableTitles = Object.keys(employeesToShow[0]).filter((key) => !excludedKeys.includes(key))
			} else {
				if (!this.users.length) return
				this.tableTitles = Object.keys(this.users[0]).filter((key) =>
					['email', 'username', 'phone', 'firstName', 'lastName', 'level', 'type'].includes(key)
				)
			}
		},
		async updateEmployeesData() {
			try {
				this.isLoadingEmployees = true
				// Fetch all contacts in the system (admin-only)
				this.employees = await getFilterdContacts({
					getAll: true
				})
				this.setTableTitles()
				this.isLoadingEmployees = false
			} catch (err) {
				console.error('Error loading employees:', err)
				swalService.errorMsg()
				this.isLoadingEmployees = false
			}
		},
		handleInput(value, searchBy) {
			if (searchBy) this.searchBy = searchBy
			this.searchTxt = value
		},
		handleCleanerAction(actionPayload) {
			if (actionPayload.type === 'view-cleaner') {
				const rowIndex = actionPayload.idx
				const rowData = this.filteredEmployees[rowIndex]
				if (rowData && rowData.id) {
					// Find the employee data by ID from the original employees array
					const employee = this.employees.find(emp => emp.id === rowData.id)
					if (employee) {
						this.currCleaner = { ...employee }
					}
				}
			}
		},
		closeCleanerModal() {
			this.currCleaner = null
		},
		getSitesList(siteNames) {
			if (!siteNames || siteNames === 'N/A') return []
			return siteNames.split(',').map(site => site.trim()).filter(site => site.length > 0)
		},
		getFilteredItems(items, field) {
			const searchTxt = String(this.modalSearchBy[field] || '').trim()
			if (!searchTxt) return items
			return util.filterArrayOfObjectsWithString(items, searchTxt, 'name')
		},
		resetModalSearchBy() {
			this.modalSearchBy = this.defaultModalSearchBy()
		},
		defaultModalSearchBy() {
			return {
				permitted_franchise: '',
				permitted_companies: '',
				permitted_sites: '',
				panel_permitted: ''
			}
		},
	},
	watch: {
		'$route.name': {
			handler(newRoute) {
				this.setTableTitles()
				// Load cleaners data when navigating to cleaners tab
				if (newRoute === 'cleaners' && this.employees.length === 0) {
					this.updateEmployeesData()
				}
			},
			immediate: true
		}
	},
	components: {
		Modal,
		Input,
		Table,
		Button,
		Select,
		Option,
		ErrorMsg,
		TableSearch,
		Loading,
		SearchBar,
		Tabs,
		Tab,
	},
}
</script>

<style lang="scss" scoped>
@use '@/styles/vars.scss' as *;
@use "@/styles/mixins" as *;

.user-page {
  position: relative;
  .custom-button-container {
    height: 80px;
    display: flex;
    align-items: center;
    margin-inline-end: 20px;
    text-transform: uppercase;
    position: absolute;
    z-index: 3;
    right: 0;
    @include rtl {
      left: 0;
      right: unset;
    }
    span {
      @include modal-input-label;
      margin-bottom: 0px;
      font-size: 13px;
    }
    input {
      @include modal-input;
      margin-inline-start: 5px;
      margin-bottom: 0px;
      padding: 0 5px;
      width: 150px;
    }
    button {
      font-size: 12px;
      font-weight: bold;
    }
  }
  .modal {
    .modal-body {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      grid-gap: 10px;
      label, .select-container {
        display: flex;
        flex-direction: column;
        font-size: 14px;
        color: #333;
        
        &.long-field {
          grid-column: span 2; /* Spans across both columns */
        }

        &.short-field {
          grid-column: span 1; /* Takes only one column */
        }

        &.full-row {
          grid-column: 1 / -1; /* Spans across the full row */
        }

        .modal-select {
          margin: 4px 0 0;
          border: 1px solid #ccc;
          border-radius: 4px;
          padding: 8px;
        }

        & > * {
			border: 1px solid $grey-border-color;
			border-radius: 4px;
			padding: 8px;
			margin: 0;
        }
      }
      &.cleaner-details {
        display: flex;
        flex-direction: column;
        gap: 20px;
        padding: 10px 0;
        
        .detail-section {
          display: flex;
          flex-direction: column;
          gap: 16px;
          
          .detail-row {
            display: flex;
            align-items: flex-start;
            padding: 12px 16px;
            background-color: #f8f9fa;
            border-radius: 8px;
            border-left: 3px solid #389ceb;
            transition: background-color 0.2s;
            
            &:hover {
              background-color: #f0f2f5;
            }
            
            .detail-label {
              font-weight: 600;
              min-width: 140px;
              color: #495057;
              margin: 0;
              font-size: 14px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              font-size: 12px;
            }
            
            .detail-value {
              color: #212529;
              font-size: 15px;
              flex: 1;
              font-weight: 500;
            }
          }
        }
        
        .sites-section {
          margin-top: 8px;
          
          .sites-label {
            font-weight: 600;
            color: #495057;
            margin-bottom: 12px;
            font-size: 14px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            font-size: 12px;
            display: block;
          }
          
          .sites-list {
            display: flex;
            flex-direction: column;
            gap: 10px;
            max-height: 300px;
            overflow-y: auto;
            padding: 12px;
            background-color: #f8f9fa;
            border-radius: 8px;
            border-left: 3px solid #28a745;
            
            // Custom scrollbar styling
            &::-webkit-scrollbar {
              width: 6px;
            }
            
            &::-webkit-scrollbar-track {
              background: #e9ecef;
              border-radius: 3px;
            }
            
            &::-webkit-scrollbar-thumb {
              background: #28a745;
              border-radius: 3px;
              
              &:hover {
                background: #218838;
              }
            }
            
            // Firefox scrollbar
            scrollbar-width: thin;
            scrollbar-color: #28a745 #e9ecef;
            
            .site-item {
              display: flex;
              align-items: center;
              gap: 12px;
              padding: 10px 12px;
              background-color: #ffffff;
              border-radius: 6px;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
              transition: transform 0.2s, box-shadow 0.2s;
              
              &:hover {
                transform: translateX(4px);
                box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
              }
              
              .site-bullet {
                width: 8px;
                height: 8px;
                border-radius: 50%;
                background-color: #28a745;
                flex-shrink: 0;
              }
              
              .site-name {
                color: #212529;
                font-size: 14px;
                font-weight: 500;
                flex: 1;
              }
            }
            
            .no-sites {
              color: #6c757d;
              font-style: italic;
              padding: 10px;
            }
          }
        }
      }
      .password-container {
        & > div {
          display: flex;
          align-items: center;
          img {
            height: 50%;
            width: 7%;
            cursor: pointer;
            margin-left: 10px;
          }
        }
      }
    }
  }
}

</style>
